/* eslint-disable -- react-hooks/immutability (React Compiler eval rule) reports false positives on immutable setState helpers in this component; the rule is not present in the app's CRA build, so a named disable cannot be used. */

import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Plus, Save, Trash2, Eraser, Grid3x3, Loader2, Users, Search, Camera, X, CalendarDays, Crown, Check, Sprout } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api, publicFileUrl, fileUrl } from '../lib/api';
import { PLANT_CATALOG, PLANT_CATEGORIES } from '../lib/data';
import { pickerCatalog, speciesVarieties } from '../lib/plantPicker';
import { bedPlantSelection, bedCellSelection } from '../lib/plantIdentity';
import presets from '../data/legacyVarietyPresets.json';
import PlantVarietyPicker from '../components/PlantVarietyPicker';
import PlantIdentityPath from '../components/PlantIdentityPath';
import { useI18n } from '../i18n';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import ShareButton from '../components/ShareButton';
import ImagePicker from '../components/ImagePicker';

const DEFAULT_ROWS = 6;
const DEFAULT_COLS = 6;

// Localised plant name (catalog covers da/en/de/sv/no; others fall back to en).
function plantName(p, locale) {
    const base = (locale || 'en').split('-')[0];
    return p.name?.[base] || p.name?.en || p.name?.da || p.id;
}

export default function BedLayout() {
    const navigate = useNavigate();
    const location = useLocation();
    const { locale, t } = useI18n();
    const [beds, setBeds] = useState([]);
    const [active, setActive] = useState(null); // current bed
    const [name, setName] = useState('');
    const [rows, setRows] = useState(DEFAULT_ROWS);
    const [cols, setCols] = useState(DEFAULT_COLS);
    const [cells, setCells] = useState([]); // [{r,c,plant_id,emoji,label}]
    const [selectedPlant, setSelectedPlant] = useState(null);
    const [mode, setMode] = useState('paint'); // 'paint' | 'erase'
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [ownVariety, setOwnVariety] = useState(false);
    const [varietyCatalog, setVarietyCatalog] = useState(null);
    const [customEmoji, setCustomEmoji] = useState('🌿');
    const [customName, setCustomName] = useState('');
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [confirmDel, setConfirmDel] = useState(false);
    const [shareDesc, setShareDesc] = useState('');
    const [plantedAt, setPlantedAt] = useState('');
    const [sharePhotos, setSharePhotos] = useState([]);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [galleryPhotos, setGalleryPhotos] = useState([]);
    const [showPhotoLibrary, setShowPhotoLibrary] = useState(false);
    const [isPublic, setIsPublic] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const lastCellTap = useRef({ key: '', at: 0 });
    const { user } = useAuth();
    const isPremium = !!user?.premium || !!user?.is_founder;

    const goBack = () => {
        if (location.key && location.key !== 'default') navigate(-1);
        else navigate('/dashboard');
    };

    // Build the public share link (creates a share token on demand) for the share sheet.
    const resolveShareUrl = async () => {
        if (!active) { toast.error(t('bed.share_first')); throw new Error('abort'); }
        const body = { description: shareDesc || name };
        if (plantedAt) body.planted_at = plantedAt;
        if (isPremium) body.images = sharePhotos;
        const r = await api.post(`/beds/${active.id}/share`, body);
        return {
            url: `${window.location.origin}/community/bed/${r.data.share_token}`,
            text: `Check out my garden bed "${name}" on GrowDaily 🌱`,
            title: `${name} · GrowDaily`,
        };
    };

    useEffect(() => {
        api.get('/plant-varieties').then(r => setVarietyCatalog(r.data)).catch(() => {});
        api.get('/beds').then((r) => {
            setBeds(r.data || []);
            if (r.data?.length > 0) loadBed(r.data[0]);
            else setName(t('bed.default_name'));
        }).catch(() => {}).finally(() => setLoading(false));
    }, []);

    const loadBed = (b) => {
        setActive(b ?? null);
        setName(b ? b.name : t('bed.default_name'));
        setRows(b ? b.rows : DEFAULT_ROWS);
        setCols(b ? b.cols : DEFAULT_COLS);
        setCells(b ? (b.cells || []) : []);
        setShareDesc(b ? (b.share_description || '') : '');
        setPlantedAt(b ? (b.planted_at || '') : '');
        setSharePhotos(b && Array.isArray(b.images) ? b.images : []);
        setIsPublic(b ? !!b.is_public : false);
    };

    const uploadPhoto = async (f) => {
        if (!f) return;
        if (!isPremium) { toast.error(t('bed.photos_premium')); return; }
        if (sharePhotos.length >= 18) { toast.error(t('bed.max_photos')); return; }
        setUploadingPhoto(true);
        try {
            const fd = new FormData();
            fd.append('file', f);
            const r = await api.post('/upload?public=true', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            setSharePhotos((p) => [...p, r.data.path]);
            toast.success(t('bed.photo_added'));
        } catch { toast.error(t('bed.upload_fail')); }
        finally { setUploadingPhoto(false); }
    };

    const openPhotoLibrary = async () => {
        setShowPhotoLibrary(true);
        try {
            const plantsResponse = await api.get('/plants');
            const plants = plantsResponse.data || [];
            const journals = await Promise.all(plants.map((plant) => api.get(`/journal/${plant.id}`).catch(() => ({ data: [] }))));
            const photos = [];
            plants.forEach((plant, index) => {
                if (plant.image_path) photos.push({ path: plant.image_path, label: plant.name });
                (journals[index]?.data || []).forEach((entry) => {
                    if (entry.image_path) photos.push({ path: entry.image_path, label: plant.name });
                });
            });
            setGalleryPhotos(Array.from(new Map(photos.map((photo) => [photo.path, photo])).values()));
        } catch { setGalleryPhotos([]); }
    };

    const chooseLibraryPhoto = async (photo) => {
        if (sharePhotos.includes(photo.path)) return;
        if (sharePhotos.length >= 18) { toast.error(t('bed.max_photos')); return; }
        try {
            await api.post('/files/publish', { path: photo.path });
            setSharePhotos((current) => [...current, photo.path]);
        } catch { toast.error(t('bed.upload_fail')); }
    };

    const cellAt = (r, c) => cells.find((x) => x.r === r && x.c === c);

    const handleCellClick = (r, c) => {
        const key = `${r}-${c}`;
        const now = Date.now();
        if (cellAt(r, c) && lastCellTap.current.key === key && now - lastCellTap.current.at < 450) {
            setCells((prev) => prev.filter((x) => !(x.r === r && x.c === c)));
            lastCellTap.current = { key: '', at: 0 };
            return;
        }
        lastCellTap.current = { key, at: now };
        if (mode === 'erase') {
            setCells((prev) => prev.filter((x) => !(x.r === r && x.c === c)));
            return;
        }
        if (!selectedPlant) {
            toast.error(t('bed.choose_plant_first'));
            return;
        }
        setCells((prev) => {
            const filtered = prev.filter((x) => !(x.r === r && x.c === c));
            return [...filtered, bedCellSelection(selectedPlant, r, c)];
        });
    };

    const persistBed = async () => {
        const payload = {
            name, rows, cols, cells,
            share_description: shareDesc,
            planted_at: plantedAt,
            images: isPremium ? sharePhotos : [],
        };
        const r = active
            ? await api.put(`/beds/${active.id}`, payload)
            : await api.post('/beds', payload);
        const fresh = r.data;
        const list = await api.get('/beds');
        setBeds(list.data || []);
        const updated = (list.data || []).find((x) => x.id === fresh.id) || fresh;
        setActive(updated);
        return updated;
    };

    const save = async () => {
        setSaving(true);
        try {
            await persistBed();
            toast.success(t('bed.saved'));
        } catch (e) {
            toast.error(t('bed.save_fail'));
        } finally { setSaving(false); }
    };

    const publishToCommunity = async () => {
        setPublishing(true);
        try {
            const fresh = await persistBed();
            const body = { description: shareDesc || name };
            if (plantedAt) body.planted_at = plantedAt;
            if (isPremium) body.images = sharePhotos;
            await api.post(`/beds/${fresh.id}/share`, body);
            const list = await api.get('/beds');
            setBeds(list.data || []);
            const updated = (list.data || []).find((x) => x.id === fresh.id);
            if (updated) setActive(updated);
            setIsPublic(true);
            toast.success(t('bed.shared_ok'));
        } catch (e) {
            toast.error(e?.response?.data?.detail || t('bed.share_fail'));
        } finally { setPublishing(false); }
    };

    const unpublish = async () => {
        if (!active) return;
        setPublishing(true);
        try {
            await api.post(`/beds/${active.id}/unshare`);
            const list = await api.get('/beds');
            setBeds(list.data || []);
            setIsPublic(false);
            toast.success(t('bed.removed_ok'));
        } catch {
            toast.error(t('bed.remove_fail'));
        } finally { setPublishing(false); }
    };

    const del = async () => {
        if (!active) return;
        setConfirmDel(false);
        try {
            await api.delete(`/beds/${active.id}`);
            const list = await api.get('/beds');
            setBeds(list.data || []);
            loadBed(null);
            toast.success(t('bed.deleted'));
        } catch {
            toast.error(t('bed.delete_fail'));
        }
    };

    const resize = (newRows, newCols) => {
        setRows(newRows);
        setCols(newCols);
        setCells((prev) => prev.filter((x) => x.r < newRows && x.c < newCols));
    };

    const importFromGarden = async () => {
        try {
            const r = await api.get('/plants');
            const myPlants = r.data || [];
            if (!myPlants.length) { toast.error(t('bed.no_garden_plants')); return; }

            const occupied = new Set(cells.map((c) => `${c.r}-${c.c}`));
            const additions = [];
            let pi = 0;
            for (let r2 = 0; r2 < 20 && pi < myPlants.length; r2 += 1) {
                for (let c2 = 0; c2 < cols && pi < myPlants.length; c2 += 1) {
                    if (occupied.has(`${r2}-${c2}`)) continue;
                    const pl = myPlants[pi];
                    pi += 1;
                    additions.push({ r: r2, c: c2, plant_id: pl.id, emoji: pl.emoji || '🌿', label: pl.name, color: '#5a7a4a' });
                }
            }
            const neededRows = additions.reduce((m, a) => Math.max(m, a.r + 1), rows);
            if (neededRows !== rows) setRows(neededRows);
            setCells((prev) => [...prev, ...additions]);
            toast.success(`${additions.length} ${t('bed.imported_suffix')}`);
        } catch {
            toast.error(t('bed.import_fail'));
        }
    };

    return (
        <div className="px-5 pt-10 pb-32 max-w-3xl mx-auto" data-testid="bed-layout-page">
            <button onClick={goBack} data-testid="btn-bed-back" className="inline-flex items-center gap-1 text-sm text-brand-ink3 hover:text-brand-ink mb-6"><ArrowLeft size={14} /> {t('common.back')}</button>
            <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">{t('bed.eyebrow')}</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-brand-ink mt-1 mb-6">{t('bed.title')}</h1>

            {/* Bed selector */}
            <div className="flex gap-2 mb-5 overflow-x-auto pb-2">
                {beds.map((b) => (
                    <button
                        key={b.id}
                        data-testid={`bed-tab-${b.id}`}
                        onClick={() => loadBed(b)}
                        className={`flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm transition-colors ${active?.id === b.id ? 'bg-brand-green text-white' : 'bg-brand-card border border-brand-border text-brand-ink hover:bg-brand-bg2'}`}
                    >
                        {b.name}
                        {b.is_public && (
                            <Sprout
                                size={13}
                                data-testid={`bed-public-${b.id}`}
                                title={t('bed.badge_public')}
                                className={active?.id === b.id ? 'text-white/90' : 'text-brand-green'}
                            />
                        )}
                    </button>
                ))}
                <button data-testid="btn-new-bed" onClick={() => loadBed(null)} className="flex-shrink-0 inline-flex items-center gap-1 px-4 py-2 rounded-full text-sm bg-brand-bg2 border border-brand-border text-brand-ink hover:bg-brand-bg">
                    <Plus size={14} /> {t('bed.new')}
                </button>
            </div>

            {/* Bed name + size controls */}
            <div className="bg-brand-card border border-brand-border rounded-3xl p-4 mb-5">
                <input
                    data-testid="bed-name-input"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-transparent border-b border-brand-border focus:border-brand-green outline-none font-serif text-xl text-brand-ink pb-1 mb-3"
                    placeholder={t('bed.name_placeholder')}
                />
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="text-xs text-brand-ink3 block mb-1">{t('bed.rows')} ({rows})</label>
                        <input type="range" min="2" max="20" value={rows} onChange={(e) => resize(parseInt(e.target.value), cols)} className="w-full" />
                    </div>
                    <div>
                        <label className="text-xs text-brand-ink3 block mb-1">{t('bed.cols')} ({cols})</label>
                        <input type="range" min="2" max="20" value={cols} onChange={(e) => resize(rows, parseInt(e.target.value))} className="w-full" />
                    </div>
                </div>
            </div>

            {/* Plant palette */}
            <div className="bg-brand-card border border-brand-border rounded-3xl p-4 mb-5">
                <div className="flex items-center justify-between mb-3">
                    <div className="text-xs uppercase tracking-widest font-semibold text-brand-ink3">{t('bed.select_plant')}</div>
                    <div className="flex gap-1">
                        <button
                            data-testid="btn-mode-paint"
                            onClick={() => setMode('paint')}
                            className={`px-3 py-1 rounded-full text-xs ${mode === 'paint' ? 'bg-brand-green text-white' : 'bg-brand-bg2 text-brand-ink'}`}
                        >🌱 {t('bed.paint')}</button>
                        <button
                            data-testid="btn-mode-erase"
                            onClick={() => setMode('erase')}
                            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs ${mode === 'erase' ? 'bg-brand-terracotta text-white' : 'bg-brand-bg2 text-brand-ink'}`}
                        ><Eraser size={11} /> {t('bed.erase')}</button>
                    </div>
                </div>

                {/* Currently-selected plant — shows the name BEFORE you place it */}
                {selectedPlant && mode === 'paint' && (
                    <div data-testid="selected-plant-banner" className="mb-3 inline-flex items-center gap-2 bg-brand-green/10 border border-brand-green/30 text-brand-green rounded-full pl-2.5 pr-3 py-1.5 text-sm">
                        <span className="text-base leading-none">{selectedPlant.emoji}</span>
                        <span className="font-medium">{selectedPlant.label}</span>
                        <span className="text-brand-ink3 text-xs hidden sm:inline">· {t('bed.tap_hint')}</span>
                    </div>
                )}

                {/* Search */}
                <div className="relative mb-3">
                    <Search size={15} strokeWidth={1.8} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-ink3" />
                    <input
                        data-testid="palette-search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t('plant_picker.search_species')}
                        aria-label={t('plant_picker.search_species')}
                        className="w-full bg-brand-bg2 border border-brand-border rounded-2xl pl-9 pr-3 py-2 text-sm outline-none focus:border-brand-green"
                    />
                </div>

                <label className="block text-sm mb-3">{t('plant_picker.choose_category')}
                    <select data-testid="palette-category" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="mt-1 w-full bg-brand-bg2 border border-brand-border rounded-xl px-3 py-2">
                        {PLANT_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.id === 'all' ? (c.name[locale] || c.name.en) : t(`plant_picker.category_${c.id}`)}</option>)}
                    </select>
                </label>

                {/* Named palette — every plant shows its name */}
                <div data-testid="plant-palette" className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-72 overflow-y-auto pr-1">
                    {(() => {
                        const varieties = Object.fromEntries(pickerCatalog(PLANT_CATALOG).map(p => [p.id, speciesVarieties(p.id, varietyCatalog, presets)]));
                        const list = pickerCatalog(PLANT_CATALOG, { search, category: categoryFilter, varieties });
                        if (list.length === 0) {
                            return <div className="col-span-full text-center text-xs text-brand-ink3 py-5">{t('bed.no_match')} “{search}”</div>;
                        }
                        return list.map((p) => {
                            const nm = plantName(p, locale);
                            const isSel = selectedPlant?.id === p.id;
                            return (
                                <button
                                    key={p.id}
                                    data-testid={`palette-${p.id}`}
                                    onClick={() => { setSelectedPlant(bedPlantSelection(p, nm)); setOwnVariety(false); setMode('paint'); }}
                                    className={`flex items-center gap-2 rounded-2xl px-2.5 py-2 text-left bg-brand-bg2 border transition-all hover:-translate-y-0.5 ${isSel ? 'border-brand-green ring-2 ring-brand-green/30' : 'border-brand-border hover:border-brand-green/50'}`}
                                    title={nm}
                                >
                                    <span className="text-xl leading-none flex-shrink-0">{p.emoji || '🌿'}</span>
                                    <span className="text-sm text-brand-ink truncate">{nm}</span>
                                </button>
                            );
                        });
                    })()}
                </div>
                {selectedPlant?.species && <div className="mt-4 space-y-3" data-testid="bed-variety-selection">
                    <PlantIdentityPath category={t(`plant_picker.category_${selectedPlant.category}`)} species={selectedPlant.speciesLabel} variety={selectedPlant.variety} />
                    <PlantVarietyPicker key={selectedPlant.species} speciesLabel={selectedPlant.speciesLabel} value={selectedPlant.variety || ''}
                        options={speciesVarieties(selectedPlant.species, varietyCatalog, presets)} addingOwn={ownVariety} setAddingOwn={setOwnVariety}
                        onChange={variety => setSelectedPlant(previous => ({ ...previous, variety, label: variety ? `${previous.speciesLabel} · ${variety}` : previous.speciesLabel }))} />
                    <p className="text-xs text-brand-ink3">{t('plant_picker.bed_plan_only')}</p>
                    {selectedPlant.category === 'mushroom' && <p className="text-sm p-3 rounded-xl bg-amber-50 text-amber-950">{t('plant_picker.mushroom_caution')}</p>}
                </div>}
                {/* Custom plant — user-defined name + emoji */}
                <div className="mt-3 pt-3 border-t border-brand-border/60">
                    <div className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-1.5">{t('bed.add_custom')}</div>
                    <div className="flex gap-2">
                        <input
                            data-testid="custom-emoji-input"
                            value={customEmoji}
                            onChange={(e) => setCustomEmoji(e.target.value.slice(0, 2))}
                            placeholder="🌶️"
                            maxLength={2}
                            className="w-12 text-center bg-brand-bg2 border border-brand-border rounded-2xl px-2 py-2 text-base"
                        />
                        <input
                            data-testid="custom-name-input"
                            value={customName}
                            onChange={(e) => setCustomName(e.target.value)}
                            placeholder={t('bed.custom_name_ph')}
                            className="flex-1 bg-brand-bg2 border border-brand-border rounded-2xl px-3 py-2 text-sm"
                        />
                        <button
                            data-testid="btn-add-custom"
                            onClick={() => {
                                const e = (customEmoji || '🌿').trim();
                                const n = customName.trim();
                                if (!n) { toast.error(t('bed.name_required')); return; }
                                setSelectedPlant({ id: `custom-${n.toLowerCase()}-${Date.now()}`, name: { da: n, en: n }, emoji: e, color: '#7a9a6a', label: n });
                                setMode('paint');
                                toast.success(`${e} ${n} ${t('bed.ready_plant')}`);
                            }}
                            className="bg-brand-green text-white px-3 py-2 rounded-2xl text-xs hover:bg-brand-greenDark"
                        >{t('common.add')}</button>
                    </div>
                </div>
            </div>

            {/* Import from My Garden */}
            <button
                data-testid="btn-import-garden"
                onClick={importFromGarden}
                className="w-full mb-5 inline-flex items-center justify-center gap-2 bg-brand-card border border-brand-border text-brand-ink py-2.5 rounded-full text-sm font-medium hover:border-brand-green/50 transition-colors"
            >
                <Sprout size={15} className="text-brand-green" /> {t('bed.import_garden')}
            </button>

            {/* The grid */}
            <div className="bg-gradient-to-br from-[#f3ead8] to-[#e8dec5] border border-brand-border rounded-3xl p-4 mb-5">
                <div
                    data-testid="bed-grid"
                    className="grid gap-1 mx-auto"
                    style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, maxWidth: `${cols * 64}px` }}
                >
                    {Array.from({ length: rows }).map((_, r) => (
                        Array.from({ length: cols }).map((_, c) => {
                            const cell = cellAt(r, c);
                            return (
                                <button
                                    key={`${r}-${c}`}
                                    data-testid={`cell-${r}-${c}`}
                                    onClick={() => handleCellClick(r, c)}
                                    onDoubleClick={() => setCells((prev) => prev.filter((x) => !(x.r === r && x.c === c)))}
                                    className={`relative aspect-square rounded-lg flex items-center justify-center text-2xl border transition-all hover:scale-110 group ${cell ? 'shadow-sm' : 'bg-white/60 border-brand-border hover:bg-white'}`}
                                    style={cell ? { backgroundColor: `${cell.color}22`, borderColor: `${cell.color}66` } : {}}
                                    title={cell?.label || ''}
                                >
                                    {cell?.emoji || ''}
                                    {cell?.label && (
                                        <span className="pointer-events-none absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] font-medium uppercase tracking-wide text-brand-ink2 bg-white/90 border border-brand-border rounded-full px-1.5 py-px whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                            {cell.label}
                                        </span>
                                    )}
                                </button>
                            );
                        })
                    ))}
                </div>
                {/* Always-visible legend of unique plants in this bed */}
                {cells.length > 0 && (
                    <div data-testid="bed-legend" className="mt-4 flex flex-wrap gap-1.5 justify-center">
                        {Array.from(new Map(cells.map((c) => [JSON.stringify([c.plant_id, c.variety || '', c.label || '']), c])).entries()).map(([key, c]) => (
                            <span key={key} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/70 border border-brand-border text-[11px] text-brand-ink2">
                                <span>{c.emoji}</span> {c.label}
                            </span>
                        ))}
                    </div>
                )}
                <div className="text-center text-xs text-brand-ink3 mt-3 inline-flex items-center justify-center w-full gap-1">
                    <Grid3x3 size={12} /> {t('bed.click_cell')} {mode === 'paint' ? t('bed.click_plant') : t('bed.click_remove')}
                </div>
            </div>

            {/* Community share settings */}
            <div className="bg-brand-card border border-brand-border rounded-2xl p-4 mb-3 space-y-3" data-testid="share-settings">
                    <div className="flex items-center gap-2">
                        <Users size={15} className="text-brand-green" />
                        <h3 className="font-serif text-base text-brand-ink">{t('bed.community_share')}</h3>
                    </div>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">{t('bed.note_label')}</span>
                        <textarea
                            data-testid="share-note"
                            value={shareDesc}
                            onChange={(e) => setShareDesc(e.target.value)}
                            rows={2}
                            maxLength={200}
                            placeholder={t('bed.note_ph')}
                            className="mt-1 w-full bg-brand-bg2 border border-brand-border rounded-xl px-3 py-2 text-sm text-brand-ink"
                        />
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 inline-flex items-center gap-1"><CalendarDays size={11} /> {t('bed.planted_label')}</span>
                        <input
                            type="date"
                            data-testid="share-planted"
                            value={plantedAt}
                            onChange={(e) => setPlantedAt(e.target.value)}
                            className="mt-1 w-full bg-brand-bg2 border border-brand-border rounded-xl px-3 py-2 text-sm text-brand-ink"
                        />
                    </label>
                    <div>
                        <span className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 inline-flex items-center gap-1">
                            <Camera size={11} /> {t('bed.photos')} {!isPremium && <Crown size={11} className="text-brand-ochre" />}
                        </span>
                        {isPremium ? (
                            <div className="flex flex-wrap gap-2 mt-1.5" data-testid="share-photos">
                                {sharePhotos.map((src, i) => (
                                    <div key={src} className="relative w-16 h-16">
                                        <img src={publicFileUrl(src)} alt="" className="w-16 h-16 object-cover rounded-xl border border-brand-border" />
                                        <button onClick={() => setSharePhotos((p) => p.filter((_, j) => j !== i))} data-testid={`remove-photo-${i}`} className="absolute -top-1.5 -right-1.5 bg-brand-terracotta text-white rounded-full p-0.5"><X size={12} /></button>
                                    </div>
                                ))}
                                {sharePhotos.length < 18 && (
                                    <ImagePicker testId="bed-photo-pick" onSelect={uploadPhoto}>
                                        {(open2) => (
                                            <button type="button" onClick={open2} data-testid="add-photo" className="w-16 h-16 rounded-xl border border-dashed border-brand-border flex items-center justify-center cursor-pointer text-brand-ink3 hover:border-brand-green hover:text-brand-green">
                                                {uploadingPhoto ? <Loader2 size={16} className="animate-spin" /> : <Camera size={18} />}
                                            </button>
                                        )}
                                    </ImagePicker>
                                )}
                                {sharePhotos.length < 18 && (
                                    <button type="button" onClick={openPhotoLibrary} className="w-16 h-16 rounded-xl border border-brand-border flex flex-col items-center justify-center text-[9px] text-brand-ink3 hover:border-brand-green hover:text-brand-green">
                                        <Sprout size={16} /><span className="mt-1">My plants</span>
                                    </button>
                                )}
                            </div>
                        ) : (
                            <button onClick={() => navigate('/premium')} data-testid="share-photos-upsell" className="mt-1.5 w-full text-left text-xs text-brand-ink3 bg-brand-bg2 border border-brand-border rounded-xl px-3 py-2 hover:border-brand-green/40">
                                {t('bed.photos_upsell')}
                            </button>
                        )}
                    </div>

                    {showPhotoLibrary && isPremium && (
                        <div className="fixed inset-0 z-[85] bg-brand-ink/45 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={() => setShowPhotoLibrary(false)}>
                            <div className="bg-brand-bg w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl border border-brand-border p-5 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center justify-between mb-4"><h3 className="font-serif text-xl text-brand-ink">Choose from My Garden</h3><button onClick={() => setShowPhotoLibrary(false)}><X size={18} /></button></div>
                                {galleryPhotos.length ? (
                                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                                        {galleryPhotos.map((photo) => (
                                            <button key={photo.path} onClick={() => chooseLibraryPhoto(photo)} className={`relative aspect-square rounded-xl overflow-hidden border-2 ${sharePhotos.includes(photo.path) ? 'border-brand-green' : 'border-transparent'}`}>
                                                <img src={fileUrl(photo.path)} alt={photo.label} className="w-full h-full object-cover" loading="lazy" />
                                                <span className="absolute inset-x-0 bottom-0 bg-brand-ink/65 text-white text-[9px] px-1 py-0.5 truncate">{photo.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                ) : <p className="text-sm text-brand-ink3 text-center py-8">No plant album photos yet.</p>}
                            </div>
                        </div>
                    )}
                    <div className="pt-2 border-t border-brand-border">
                        {isPublic ? (
                            <div className="flex items-center justify-between gap-2" data-testid="community-status-live">
                                <span className="inline-flex items-center gap-1.5 text-sm text-brand-green font-medium"><Check size={15} /> {t('bed.live')}</span>
                                <button
                                    onClick={unpublish}
                                    disabled={publishing}
                                    data-testid="btn-unpublish-community"
                                    className="text-xs text-brand-terracotta hover:underline disabled:opacity-50"
                                >
                                    {publishing ? t('bed.working') : t('bed.remove')}
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={publishToCommunity}
                                disabled={publishing}
                                data-testid="btn-publish-community"
                                className="w-full inline-flex items-center justify-center gap-2 bg-brand-green hover:bg-brand-greenDark text-white py-2.5 rounded-full text-sm font-medium disabled:opacity-50 transition-colors"
                            >
                                {publishing ? <Loader2 size={15} className="animate-spin" /> : <Users size={15} />} {t('bed.share_to_community')}
                            </button>
                        )}
                        <p className="text-[11px] text-brand-ink3 mt-2">{t('bed.name_date_note')} {isPublic ? t('bed.live_hint') : t('bed.publish_hint')}</p>
                    </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 flex-wrap">
                <button
                    data-testid="btn-save-bed"
                    onClick={save}
                    disabled={saving}
                    className="flex-1 min-w-[140px] inline-flex items-center justify-center gap-2 bg-brand-green hover:bg-brand-greenDark text-white py-3 rounded-full font-medium disabled:opacity-50"
                >
                    {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {active ? t('bed.update') : t('bed.save')}
                </button>
                {active && (
                    <>
                        <ShareButton
                            resolveUrl={resolveShareUrl}
                            title={`${name} · GrowDaily`}
                            text={`Check out my garden bed "${name}" on GrowDaily 🌱`}
                            label={t('common.share')}
                            className="bg-brand-card border border-brand-border px-4 py-3 rounded-full text-brand-ink hover:bg-brand-bg2"
                        />
                        <button
                            data-testid="btn-delete-bed"
                            onClick={() => setConfirmDel(true)}
                            className="inline-flex items-center gap-1 bg-brand-terracotta/10 text-brand-terracotta border border-brand-terracotta/30 px-4 py-3 rounded-full text-sm hover:bg-brand-terracotta/20"
                        >
                            <Trash2 size={14} /> {t('bed.delete')}
                        </button>
                    </>
                )}
            </div>

            {confirmDel && active && (
                <div className="fixed inset-0 z-[75] bg-brand-ink/45 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={() => setConfirmDel(false)} data-testid="confirm-delete-bed">
                    <div className="bg-brand-bg w-full sm:max-w-sm rounded-t-3xl sm:rounded-3xl border border-brand-border p-6 animate-fade-up" onClick={(e) => e.stopPropagation()}>
                        <div className="w-12 h-12 rounded-2xl bg-brand-terracotta/12 text-brand-terracotta flex items-center justify-center mb-3"><Trash2 size={22} /></div>
                        <h3 className="font-serif text-xl text-brand-ink">{t('bed.delete_q')}</h3>
                        <p className="text-sm text-brand-ink2 mt-1">“{active.name}” {t('bed.delete_desc')}</p>
                        <div className="flex gap-2 mt-5">
                            <button onClick={() => setConfirmDel(false)} className="flex-1 py-3 rounded-full text-sm bg-brand-card border border-brand-border text-brand-ink hover:bg-brand-bg2">{t('common.cancel')}</button>
                            <button data-testid="btn-confirm-delete-bed" onClick={del} className="flex-1 py-3 rounded-full text-sm bg-brand-terracotta text-white hover:opacity-90">{t('bed.delete')}</button>
                        </div>
                    </div>
                </div>
            )}
            <button
                onClick={() => navigate('/community')}
                data-testid="btn-browse-community"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 text-sm text-brand-ink3 hover:text-brand-ink"
            >
                <Users size={14} /> {t('bed.browse')}
            </button>

            {loading && <div className="text-center text-xs text-brand-ink3 mt-4">{t('common.loading')}</div>}
        </div>
    );
}
